/**
 * API Client — cookie-based session auth (CB25SES-1197/1198/1206)
 *
 * The Go backend's /auth/login (redirect to Keycloak) + /auth/callback
 * (code exchange) set an HttpOnly session cookie (`ses_at`) once login
 * completes, and the backend bridges that cookie to `Authorization: Bearer`
 * on the server side for every subsequent request (see cookieAuthFilter in
 * the backend's internal/server/http.go). The frontend therefore never
 * reads, stores, or attaches a JWT itself — every request just needs
 * `credentials: 'include'` so the browser sends the cookie along.
 *
 * This replaces the old "Phase 1" guest-JWT bootstrap (POST
 * /users/generate-token + localStorage token pair), which called an
 * endpoint the backend no longer exposes.
 */

import { navigateTo } from './navigation';
import { ErrorCodes, errorCodeFromBody } from '../api/errorCodes';
import {
  NO_ACCESS_PATH,
  NO_ORGANIZATION_REASON,
  onNoAccessPage,
} from './noAccess';

// Relative, in every environment. The Go backend serves these paths itself and
// nothing between the browser and it rewrites them, so an endpoint has one
// spelling everywhere — see docs/api-path-layout.md in the backend repo.
// Deployed, nginx forwards the prefix; locally, the dev server proxy does.
const API_BASE_URL = '/api/ces';
const API_TIMEOUT = 60000; // 60 seconds

/** True when the browser is already showing the session-expired screen. */
function onSessionExpiredScreen(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return (
      new URLSearchParams(window.location.search).get('reason') ===
      'session-expired'
    );
  } catch {
    return false;
  }
}
/**
 * API Error response format
 */
interface ApiErrorResponse {
  code?: string;
  reason?: string;
  message: string;
  details?: string;
  timestamp?: number;
  metadata?: Record<string, string>;
}

/**
 * Custom API Error class
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string | undefined,
    message: string,
    public details?: string,
    /** 5-digit error code shown to the user (CB25SES-1804). */
    public errorCode?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Parses error response from server
 */
async function parseErrorResponse(
  response: Response,
): Promise<ApiErrorResponse> {
  try {
    return await response.json();
  } catch {
    return {
      message: response.statusText || 'Unknown error',
      code: `HTTP_${response.status}`,
    };
  }
}

/**
 * Sends the browser straight to the backend's Keycloak-backed login.
 * Used on 401s so a lapsed/missing session bounces to Keycloak instead of
 * the app rendering a broken, half-authenticated screen.
 *
 * return_to carries this page's absolute URL so the callback, which completes
 * on the backend's host, lands the browser back here. The backend only honours
 * it within its configured cookie domain; where that is unset (local dev) it is
 * dropped and you return manually.
 */
export function redirectToLogin(returnTo: string = currentUrl()): void {
  const params = new URLSearchParams({ return_to: returnTo });
  navigateTo(`/auth/login?${params.toString()}`);
}

/** This page's absolute URL, for return_to. */
export function currentUrl(): string {
  return `${window.location.origin}${window.location.pathname}${window.location.search}`;
}

/**
 * Clears the backend session cookie and redirects to Keycloak's logout.
 */
export function logout(postLogoutPath: string = '/login'): void {
  navigateTo(
    `/auth/logout?post_logout_redirect_uri=${encodeURIComponent(
      postLogoutPath,
    )}`,
  );
}

/**
 * Cheap, best-effort probe for an existing backend session. Used by the
 * product's login screen to skip the sign-in prompt if the user already
 * completed the Keycloak round trip (e.g. in another tab, or after manually
 * navigating back per the return_to gap noted above).
 */
export async function hasActiveSession(): Promise<boolean> {
  try {
    const res = await fetch('/auth/session', {
      method: 'GET',
      credentials: 'include',
    });
    if (res.status === 401) {
      return false;
    }
    // The endpoint answers JSON. A dev server with no backend configured
    // serves index.html for any path, which would otherwise read as a session.
    return (
      res.headers.get('content-type')?.includes('application/json') ?? false
    );
  } catch {
    return false;
  }
}

/**
 * Main API call function. Always sends the session cookie
 * (`credentials: 'include'`); on 401 it redirects to login instead of
 * returning to the caller. `timeoutMs` overrides the default for calls that
 * legitimately take longer, such as large uploads.
 */
export async function apiCall(
  url: string,
  options: RequestInit = {},
  timeoutMs: number = API_TIMEOUT,
): Promise<Response> {
  const headers = new Headers({
    'Content-Type': 'application/json',
    ...options.headers,
  });

  // Create AbortController for timeout. If the caller already passed a signal,
  // we honour it by aborting our own controller when it fires.
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  if (options.signal) {
    options.signal.addEventListener('abort', () => controller.abort());
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
      signal: controller.signal,
    });

    // No session (missing/expired cookie) — redirect to the session-expired
    // screen instead of bouncing directly to Keycloak (which fails locally
    // when no Keycloak session exists).
    if (response.status === 401) {
      // Only navigate if we are not already on the session-expired screen.
      // Without this guard a 401 raised from that screen redirects to itself,
      // and because navigateTo is a full-page load the request fires again on
      // arrival — an endless reload. It became reachable once a provider
      // mounted app-wide started fetching on every route, including "/".
      if (!onSessionExpiredScreen()) {
        navigateTo('/?reason=session-expired');
      }
      throw new ApiError(
        401,
        'UNAUTHORIZED',
        'Authentication required',
        undefined,
        ErrorCodes.sessionExpired,
      );
    }

    // Handle other HTTP errors
    if (!response.ok) {
      const errorData = await parseErrorResponse(response);
      // Signed in, but no organization: a login would succeed again, so show
      // the no-access page instead.
      if (
        response.status === 403 &&
        errorData.reason === NO_ORGANIZATION_REASON &&
        !onNoAccessPage()
      ) {
        navigateTo(NO_ACCESS_PATH);
      }
      throw new ApiError(
        response.status,
        errorData.code,
        errorData.message,
        errorData.details,
        errorCodeFromBody(errorData) ?? ErrorCodes.unexpected,
      );
    }

    return response;
  } catch (err) {
    if (err instanceof ApiError) {
      throw err;
    }
    if (err instanceof TypeError && err.message.includes('Failed to fetch')) {
      // Network error
      throw new ApiError(
        0,
        'NETWORK_ERROR',
        'Failed to connect to server',
        undefined,
        ErrorCodes.network,
      );
    }
    if (err instanceof DOMException && err.name === 'AbortError') {
      // Request timeout
      throw new ApiError(
        0,
        'REQUEST_TIMEOUT',
        'Request timed out',
        undefined,
        ErrorCodes.timeout,
      );
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Convenience methods for common HTTP verbs
 */
export async function apiGet(
  url: string,
  options?: RequestInit,
): Promise<Response> {
  return apiCall(url, { ...options, method: 'GET' });
}

export async function apiPost(
  url: string,
  body?: unknown,
  options?: RequestInit,
): Promise<Response> {
  return apiCall(url, {
    ...options,
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
}

export async function apiPut(
  url: string,
  body?: unknown,
  options?: RequestInit,
): Promise<Response> {
  return apiCall(url, {
    ...options,
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  });
}

export async function apiDelete(
  url: string,
  options?: RequestInit,
): Promise<Response> {
  return apiCall(url, { ...options, method: 'DELETE' });
}

export { API_BASE_URL };
